import { useRef, useState, type ReactNode } from 'react';

import { Alert, AlertTitle, Box, Container, Stack } from '@mui/material';

import type {
  ChatMessage,
  ConversationalFormExplanation,
  ConversationalFormOption,
  ConversationalFormStep,
} from '@karehero/models';

import { Button } from '@/components/atoms/Button/Button';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { ActionRows } from './ActionRows';
import {
  ChatComposer,
  type CardEditing,
  type ChatComposerHandle,
  type RecorderProps,
  type VoiceControl,
} from './ChatComposer';
import { FooterTransition, type FooterKind } from './FooterTransition';
import { MessageThread, type ReplyTone } from './MessageThread';
import { ORB_CLEARANCE } from './OrbDock';
import { QuickReplies } from './QuickReplies';
import { answersArrivalSx } from './arrival';
import { ExplanationSheet } from './ExplanationSheet';
import { ScreenLayout } from './ScreenLayout';
import { UploadSheet, type UploadSource } from './UploadSheet';
import {
  ABOVE_DIM_OVERLAY_Z_INDEX,
  DimOverlay,
} from '@/pwa/atoms/DimOverlay/DimOverlay';

export interface ConversationalFormNotice {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}

/**
 * How a step's options are offered: rows of their own in the thread, chips
 * above the text field, or buttons attached to the question.
 */
export type ChoiceStyle = 'rows' | 'chips' | 'attached';

export interface ConversationalFormPageProps {
  messages: ChatMessage[];
  /** The step awaiting an answer. Omit once nothing is left to ask. */
  currentStep?: ConversationalFormStep;
  /** Something for the person to act on, without ending the conversation. */
  notice?: ConversationalFormNotice;
  /** Blocks every input while an answer is being written away. */
  isSubmitting?: boolean;
  /** The next question is on its way: shows the dots and holds the inputs. */
  isThinking?: boolean;
  onChoose: (option: ConversationalFormOption) => void;
  /** Omit when no step is multi-select. */
  onChooseMany?: (options: ConversationalFormOption[]) => void;
  onSend: (text: string, photos: File[]) => void;
  /** Photos waiting in the composer to go with the next message. */
  photos?: File[];
  setPhotos?: (photos: File[]) => void;
  /** Omit to hide the back button. */
  onBack?: () => void;
  /** Omit to hide the voice affordance on `expand` steps. */
  recorder?: RecorderProps;
  /** Omit to hide the attachment affordance. Called with what the upload sheet chose. */
  onAttachFile?: (source: UploadSource) => void;
  /** Omit when a screen step's note link cannot be opened. */
  onOpenLink?: (url: string) => void;
  onOpenDocument?: (documentId: string) => void;
  getDocumentUrl?: (documentId: string) => Promise<string | undefined>;
  /** Omit when a message that did not send cannot be tried again. */
  onResend?: (messageId: string) => void;
  /** Defaults to the orb, offered on open questions. The button is offered wherever voice is. */
  voiceControl?: VoiceControl;
  /** Defaults to rows. Chips leave the text field open, so an answer can be typed or said instead. */
  choiceStyle?: ChoiceStyle;
  /** Shown at the end of the thread, before any rows: what is happening right now. */
  threadEnd?: ReactNode;
  /** Held above the thread while it is being worked through, such as a checklist. */
  pinned?: ReactNode;
  /** Opens a card's lines to be corrected, for a card that offers it. */
  onEditCard?: (messageId: string) => void;
  /** Pins or unpins a card, for a card that offers it. */
  onPinCard?: (messageId: string) => void;
  /** Hears what is typed in the composer as it changes, before it is sent. */
  onDraftChange?: (text: string) => void;
  /** Hears the message field lose focus, as when the keyboard is closed. */
  onDraftBlur?: () => void;
  /** Defaults to coral. */
  replyTone?: ReplyTone;
  /** The menu message asking the current step, whose rows answer it in place of rows of its own. */
  openMenuId?: string;
  /** Changes a menu's answer, for a menu that offers it. */
  onChangeMenuAnswer?: (
    messageId: string,
    option: ConversationalFormOption,
  ) => void;
  /** Whether voice notes show their words; one setting for the whole chat. */
  isVoiceTextShown?: boolean;
  /** Shows or hides voice notes' words; omit to offer no words. */
  onToggleVoiceText?: () => void;
  /** Dims the chat behind the pinned card, so it and the bar are what is left; omit to leave it undimmed. */
  isPinnedFocused?: boolean;
  /** A card's lines being corrected in the message field, in place of a sheet. */
  editingCard?: CardEditing;
  /** A card held at the top of the chat once scrolled to, in its place in the thread rather than above it; while one is, `pinned` is not dimmed. */
  heldCardId?: string;
}

export const ConversationalFormPage = ({
  messages,
  currentStep,
  notice,
  isSubmitting,
  isThinking = false,
  onChoose,
  onChooseMany,
  onSend,
  photos,
  setPhotos,
  onBack,
  recorder,
  onAttachFile,
  onOpenLink,
  onOpenDocument,
  getDocumentUrl,
  onResend,
  voiceControl = 'orb',
  choiceStyle = 'rows',
  threadEnd,
  pinned,
  onEditCard,
  onPinCard,
  onDraftChange,
  onDraftBlur,
  replyTone,
  openMenuId,
  onChangeMenuAnswer,
  isVoiceTextShown,
  onToggleVoiceText,
  isPinnedFocused,
  editingCard,
  heldCardId,
}: ConversationalFormPageProps) => {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [explanation, setExplanation] =
    useState<ConversationalFormExplanation>();
  const composerRef = useRef<ChatComposerHandle>(null);
  const isBusy = isSubmitting || isThinking;
  // Only a page that says when to dim the chat behind its pinned card gets the
  // dim overlay; a card held in the thread dims it there instead, never both.
  const isDimmable = !!pinned && isPinnedFocused !== undefined && !heldCardId;

  if (currentStep?.presentation === 'screen') {
    return (
      <ScreenLayout
        title={currentStep.title ?? currentStep.ask}
        body={currentStep.title ? currentStep.ask : undefined}
        points={currentStep.points}
        note={currentStep.note}
        imageSrc={currentStep.imageSrc}
        options={currentStep.options ?? []}
        isSubmitting={isBusy}
        onBack={onBack}
        onChoose={onChoose}
        onOpenLink={onOpenLink}
      />
    );
  }

  const isVoiceOffered =
    !!currentStep?.isVoiceEnabled &&
    (currentStep.mode === 'expand' || voiceControl === 'button');
  const composerRecorder = isVoiceOffered ? recorder : undefined;

  // Chips stand in for single-choice rows; picking several still needs rows.
  const isChips =
    choiceStyle === 'chips' &&
    !!currentStep?.options?.length &&
    !currentStep.isMultiSelect;

  const openUpload = () => setIsUploadOpen(true);

  const handleChooseUpload = (source: UploadSource) => {
    setIsUploadOpen(false);
    onAttachFile?.(source);
  };

  // A row can lead to a file, or explain something, rather than answer.
  const handleChoose = (option: ConversationalFormOption) => {
    if (option.opensFilePicker && onAttachFile) {
      openUpload();
      return;
    }
    if (option.explain) {
      setExplanation(option.explain);
      return;
    }
    // A row that ends an open question sends what is still in the composer.
    if (currentStep?.mode === 'expand') composerRef.current?.flushDraft();
    onChoose(option);
  };

  // Held back while a row still offers to open it, so the question reads as
  // a choice between its rows rather than a text field.
  const isInputHeldBack =
    !isChips && !!currentStep?.options?.some((option) => option.opensInput);

  // `choose` steps are answered in the thread, so no footer, unless their
  // answers are chips over the field.
  const footerKind: FooterKind =
    ((currentStep?.mode === 'expand' ||
      currentStep?.mode === 'say' ||
      isChips) &&
      !isInputHeldBack) ||
    !!editingCard
      ? 'composer'
      : 'none';
  const footer =
    footerKind === 'composer' && currentStep ? (
      // No Divider: the orb straddles the top edge.
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'custom.neutral.200',
        }}
      >
        <Container maxWidth='sm' disableGutters>
          {/* Not keyed by step: the composer stays mounted from one expand
              step to the next, so the orb arrives on the bar already there. */}
          {isChips &&
            !isBusy &&
            !editingCard &&
            composerRecorder?.state !== 'recording' && (
              // Keyed by step, so each question's chips arrive after it does.
              <Box
                key={currentStep.id}
                sx={[{ px: 3, pt: 1.5 }, answersArrivalSx]}
              >
                <QuickReplies
                  options={currentStep.options ?? []}
                  onChoose={handleChoose}
                />
              </Box>
            )}
          <ChatComposer
            stepId={currentStep.id}
            placeholder={currentStep.placeholder}
            inputType={currentStep.inputType}
            recorder={composerRecorder}
            voiceControl={voiceControl}
            isFileEnabled={currentStep.isFileEnabled}
            isSubmitting={isBusy}
            photos={photos}
            setPhotos={setPhotos}
            onSend={onSend}
            onAttachFile={onAttachFile && openUpload}
            handleRef={composerRef}
            onDraftChange={onDraftChange}
            onDraftBlur={onDraftBlur}
            editing={editingCard}
          />
        </Container>
      </Box>
    ) : null;

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <FooterTransition kind={footerKind}>{footer}</FooterTransition>
      }
    >
      <Stack
        sx={{
          height: '100%',
          width: '100%',
          maxWidth: 'sm',
          mx: 'auto',
          // The dim overlay behind a pinned card covers the chat, not the bar.
          ...(isDimmable && { position: 'relative' }),
        }}
      >
        {isDimmable && <DimOverlay isShown={!!isPinnedFocused} />}
        {pinned && (
          <Box
            sx={{
              px: 3,
              pt: 2,
              flexShrink: 0,
              // Above the dim overlay, so it and the bar are what is left.
              ...(isDimmable && {
                position: 'relative',
                zIndex: ABOVE_DIM_OVERLAY_Z_INDEX,
              }),
            }}
          >
            {pinned}
          </Box>
        )}
        <MessageThread
          messages={messages}
          onEditCard={onEditCard}
          onPinCard={onPinCard}
          replyTone={replyTone}
          openMenuId={openMenuId}
          onChooseFromMenu={handleChoose}
          onChangeMenuAnswer={onChangeMenuAnswer}
          onExplain={setExplanation}
          isVoiceTextShown={isVoiceTextShown}
          onToggleVoiceText={onToggleVoiceText}
          heldCardId={heldCardId}
          isHeldDimming={isPinnedFocused}
          isTyping={isThinking}
          onOpenDocument={onOpenDocument}
          getDocumentUrl={getDocumentUrl}
          onResend={onResend}
          // The thread runs under the orb; its end scrolls clear of it.
          endSpace={
            composerRecorder && voiceControl === 'orb' ? ORB_CLEARANCE : 0
          }
          // The answered step's rows would be stale while the next lands.
          footer={
            <>
              {threadEnd && (
                <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
                  {threadEnd}
                </Stack>
              )}
              {!isBusy && currentStep && !isChips && !openMenuId && (
                <ActionRows
                  key={currentStep.id}
                  step={currentStep}
                  layout={choiceStyle === 'attached' ? 'attached' : 'rows'}
                  isSubmitting={
                    isBusy || composerRecorder?.state === 'recording'
                  }
                  onChoose={handleChoose}
                  onChooseMany={onChooseMany}
                />
              )}
            </>
          }
        />
        {notice && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <Alert severity='success' icon={false}>
              <AlertTitle>{notice.title}</AlertTitle>
              <Stack spacing={2}>
                <Box>{notice.body}</Box>
                <Button
                  variant='primary'
                  size='md'
                  isFullWidth
                  ariaLabel={notice.actionLabel}
                  onPress={notice.onAction}
                >
                  {notice.actionLabel}
                </Button>
              </Stack>
            </Alert>
          </Box>
        )}
      </Stack>
      <UploadSheet
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onChoose={handleChooseUpload}
      />
      <ExplanationSheet
        explanation={explanation}
        onClose={() => setExplanation(undefined)}
        onOpenLink={onOpenLink}
      />
    </ModalPageTemplate>
  );
};

ConversationalFormPage.displayName = 'ConversationalFormPage';
export default ConversationalFormPage;
